import { useState } from "react";

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { useApi } from "../hooks/useApi";

const AddServiceDialog = ({
  showAddServiceDialog,
  setShowAddServiceDialog,
  showServiceAlert,
  setServiceShowAlert,
  onSave,
}) => {
  const api = useApi();

  const [service, setService] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  const handleClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setShowAddServiceDialog(false);
    }
  };

  const addService = async () => {
    setSubmitted(true);
    setIsSubmitting(true);
    setShowAlert({ show: false, message: "", type: "" });

    try {
      await api("/tenants/services.php", {
        method: "POST",
        body: JSON.stringify(service),
      });

      setServiceShowAlert({
        show: true,
        message: "Successfully added service.",
        type: "success",
      });
      setShowAddServiceDialog(false);
      onSave();
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={showAddServiceDialog}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Add Service
        <IconButton
          disabled={isSubmitting}
          aria-label="close"
          onClick={() => setShowAddServiceDialog(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {showAlert.show === true && (
          <Alert sx={{ mb: 2 }} severity={showAlert.type}>
            {showAlert.message}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Service Name
            </Typography>
            <TextField
              variant="outlined"
              name="serviceName"
              label=""
              fullWidth
              placeholder="Service Name"
              value={service?.name || ""}
              disabled={isSubmitting}
              error={submitted && !service?.name}
              helperText={
                submitted && !service?.name ? "Service name is required" : ""
              }
              onChange={(e) => {
                setService((prev) => ({ ...prev, name: e.target.value }));
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Service Description{" "}
              <Typography
                component="span"
                variant="caption"
                sx={{ color: "text.secondary", ml: 0.5 }}
              >
                (optional)
              </Typography>
            </Typography>
            <TextField
              variant="outlined"
              name="serviceDescription"
              label=""
              fullWidth
              placeholder="Service Description"
              value={service?.description || ""}
              disabled={isSubmitting}
              onChange={(e) => {
                setService((prev) => ({
                  ...prev,
                  description: e.target.value,
                }));
              }}
            />
          </Grid>
          <Grid size={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Service Price
            </Typography>
            <TextField
              variant="outlined"
              name="servicePrice"
              label=""
              fullWidth
              type="number"
              placeholder="Service Price"
              value={service?.price || ""}
              disabled={isSubmitting}
              error={submitted && !service?.price}
              helperText={
                submitted && !service?.price ? "Service price is required" : ""
              }
              slotProps={{
                input: {
                  startAdornment: <InputAdornment>$</InputAdornment>,
                },
              }}
              onChange={(e) => {
                setService((prev) => ({
                  ...prev,
                  price: e.target.value,
                }));
              }}
            />
          </Grid>
          <Grid size={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Service Duration
            </Typography>
            <TextField
              variant="outlined"
              name="serviceDuration"
              label=""
              fullWidth
              type="number"
              placeholder="Service Duration (minutes)"
              value={service?.duration_minutes || ""}
              disabled={isSubmitting}
              error={submitted && !service?.duration_minutes}
              helperText={
                submitted && !service?.duration_minutes
                  ? "Service duration is required"
                  : ""
              }
              slotProps={{
                input: {
                  endAdornment: <InputAdornment>mins</InputAdornment>,
                },
              }}
              onChange={(e) => {
                setService((prev) => ({
                  ...prev,
                  duration_minutes: e.target.value,
                }));
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setShowAddServiceDialog(false)}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => addService()}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress
              enableTrackSlot
              size="25px"
              sx={{ color: "text.primary" }}
            />
          ) : (
            "Add Service"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddServiceDialog;
